/**
 * useDebouncedError — suppresses transient errors that clear within
 * `debounceMs`. The banner only paints if the error has been live longer
 * than the debounce. A `dismiss()` setter hides the currently-visible
 * error; a fresh error (different code OR new identity reference) clears
 * the dismissal.
 *
 * The point: SSE reconnect transients (token refresh races, brief network
 * hiccups) are mostly self-healing within ~1s. Painting a red banner for
 * 300ms several times a minute is just noise. Errors that persist past
 * the debounce are real and the banner stays until manually dismissed.
 */
import { useEffect, useRef, useState } from 'react';

export interface ErrorPayload {
  code: string;
  message: string;
}

interface UseDebouncedErrorResult {
  visible: ErrorPayload | null;
  dismiss: () => void;
}

export function useDebouncedError(
  error: ErrorPayload | null,
  debounceMs = 1500,
): UseDebouncedErrorResult {
  const [visible, setVisible] = useState<ErrorPayload | null>(null);
  // Track the last code we dismissed so we don't keep re-showing it.
  const dismissedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    if (error === null) {
      // Underlying error cleared → drop the banner immediately, reset dismissal.
      setVisible(null);
      dismissedCodeRef.current = null;
      return undefined;
    }

    // If this error is the one we dismissed, keep it hidden.
    if (dismissedCodeRef.current === error.code) {
      return undefined;
    }

    // Different error from what we dismissed → re-enable.
    if (dismissedCodeRef.current && dismissedCodeRef.current !== error.code) {
      dismissedCodeRef.current = null;
    }

    const timer = window.setTimeout(() => {
      setVisible(error);
    }, debounceMs);
    return () => window.clearTimeout(timer);
  }, [error, debounceMs]);

  function dismiss(): void {
    if (visible) {
      dismissedCodeRef.current = visible.code;
    }
    setVisible(null);
  }

  return { visible, dismiss };
}
